/**
 * Modal Component - Foundational Primitive #15
 *
 * Base modal dialog with backdrop. Uses CSS primitive classes from:
 * - 11-modal.css for modal structure
 * - 02-card.css for card styling with sharp corners
 *
 * All modals use sharp corners (border-radius: 0) and maroon-tinted shadows
 * per DO THIS/NOT THAT guidelines.
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useThemeClasses, cn } from '../../hooks/useThemeClasses';
import Button from './Button';
import '../../theme-components/11-modal.css';
import '../../theme-components/02-card.css';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  customHeader?: React.ReactNode; // New prop for custom header content
  children: React.ReactNode;
  footer?: React.ReactNode;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  maxWidth?: string;
  topOffset?: string;
  className?: string;
  fullSize?: boolean; // New prop to control full-size mode
  noPadding?: boolean; // New prop to remove content padding
  onContentRefReady?: (scrollToTop: () => void) => void; // New prop to provide scroll function
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  customHeader,
  children,
  footer,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  maxWidth = '1200px',
  topOffset = '60px',
  className = '',
  fullSize = false,
  noPadding = false,
  onContentRefReady
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const tc = useThemeClasses();
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Provide scroll function to parent when modal is open
  useEffect(() => {
    if (isOpen && contentRef.current && onContentRefReady) {
      console.log('🔝 Modal - Creating scroll function, contentRef:', contentRef.current);
      const scrollToTop = () => {
        console.log('🔝 Modal - Scroll function called, contentRef.current:', contentRef.current);
        if (contentRef.current) {
          console.log('🔝 Modal - Scrolling content to top, scrollTop before:', contentRef.current.scrollTop);
          contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          setTimeout(() => {
            console.log('🔝 Modal - ScrollTop after scroll:', contentRef.current?.scrollTop);
          }, 100);
        }
      };
      console.log('🔝 Modal - Passing scroll function to parent:', scrollToTop);
      onContentRefReady(scrollToTop);
    }
  }, [isOpen, onContentRefReady]);

  // Handle click outside modal
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        // CSS Primitive: .modal-backdrop from 11-modal.css
        'modal-backdrop',
        'fixed left-0 right-0 flex justify-center z-[1000]',
        fullSize ? 'top-0 bottom-0' : (topOffset === '0' ? 'items-center top-0 bottom-0' : 'items-start top-0 bottom-0'),
        fullSize ? 'bg-transparent p-0' : 'p-5',
        className
      )}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={cn(
          // CSS Primitive: .themed-card from 02-card.css (sharp corners, shadow)
          fullSize ? '' : 'themed-card',
          'w-full flex flex-col overflow-hidden',
          tc.bg.surface,
          fullSize
            ? 'sharp-corners-none shadow-none max-w-none h-full'
            : 'h-auto max-h-[calc(100vh-120px)]'
        )}
        style={{
          maxWidth: fullSize ? 'none' : maxWidth
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header - Fixed at top */}
        {(customHeader || title || showCloseButton) && (
          <div
            className={cn(
              'flex justify-center border-b flex-shrink-0 relative',
              tc.bg.surface,
              tc.border.inactive,
              'p-3',
              customHeader ? 'items-start' : 'items-center',
              fullSize ? 'sharp-corners-none' : 'sharp-corners-t-xl'
            )}
          >
            {customHeader ? (
              <div className={cn('w-full', showCloseButton && 'sm:pr-4')}>
                {customHeader}
              </div>
            ) : (
              title && (
                <h2 className={cn(
                  'text-2xl font-semibold m-0',
                  tc.text.primary,
                  tc.font.primary,
                  showCloseButton && 'sm:pr-4'
                )}>
                  {title}
                </h2>
              )
            )}

            {showCloseButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                aria-label={t('modal.closeModal')}
                className="absolute"
                style={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                  padding: 0,
                  borderRadius: '50%',
                  fontSize: '20px',
                  top: '0.75rem',
                  right: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </Button>
            )}
          </div>
        )}

        {/* Modal Content - Scrollable with flex-1 to fill space */}
        <div
          ref={contentRef}
          className={cn(
            'flex-1 overflow-auto min-h-0',
            fullSize || noPadding ? 'p-0' : 'py-3 px-3 sm:p-8'
          )}
          data-modal-content
          style={{ position: 'relative' }}
        >
          {children}
        </div>

        {/* Modal Footer - Fixed at bottom */}
        {footer && (
          <div className={cn(
            'border-t flex-shrink-0',
            tc.bg.surface,
            tc.border.inactive,
            fullSize ? 'sharp-corners-none' : 'sharp-corners-b-xl'
          )}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;